import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, AlertCircle, Clock, ExternalLink, CheckCircle, X, Calendar, User, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

// Flag indicator badge component
export function FlagIndicator({ yellowCount = 0, redCount = 0, onClick }) {
  if (yellowCount === 0 && redCount === 0) return null;
  
  return (
    <div className="flex items-center gap-2 cursor-pointer" onClick={onClick} data-testid="flag-indicator">
      {redCount > 0 && (
        <Badge variant="destructive" className="flex items-center gap-1 animate-pulse">
          <AlertCircle className="w-3 h-3" />
          {redCount} Red Flag{redCount > 1 ? 's' : ''}
        </Badge>
      )}
      {yellowCount > 0 && (
        <Badge className="bg-yellow-500 hover:bg-yellow-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {yellowCount} Yellow Flag{yellowCount > 1 ? 's' : ''}
        </Badge>
      )}
    </div>
  );
}

// Patient flags banner - shows at top of patient detail page
export function PatientFlagsBanner({ patientId }) {
  const { api, user } = useAuth();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchFlags();
  }, [patientId]);

  const fetchFlags = async () => {
    try {
      const response = await api.get(`/clinical-flags/patient/${patientId}`);
      setFlags(response.data);
    } catch (error) {
      console.error('Failed to fetch flags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (flagId) => {
    try {
      await api.post(`/clinical-flags/resolve/${flagId}`);
      toast.success('Flag resolved');
      fetchFlags();
    } catch (error) {
      toast.error('Failed to resolve flag');
    }
  };

  if (loading || flags.length === 0) return null;

  // Don't show flags to parents
  if (user?.role === 'parent') return null;

  const redFlags = flags.filter(f => f.severity === 'red');
  const yellowFlags = flags.filter(f => f.severity === 'yellow');

  return (
    <>
      <Card className={`mb-4 border-2 ${redFlags.length > 0 ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'}`} data-testid="patient-flags-banner">
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {redFlags.length > 0 ? (
                <AlertCircle className="w-6 h-6 text-red-600 animate-pulse" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              )}
              <div>
                <p className={`font-semibold ${redFlags.length > 0 ? 'text-red-800' : 'text-yellow-800'}`}>
                  Documentation Overdue
                </p>
                <p className="text-sm text-slate-600">
                  {flags.length} overdue item{flags.length > 1 ? 's' : ''} requiring attention
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setDialogOpen(true)}
              data-testid="view-flags-btn"
            >
              View Details
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Clinical Safety Flags
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {flags.map((flag) => (
              <Card 
                key={flag.id} 
                className={`${flag.severity === 'red' ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {flag.severity === 'red' ? (
                          <Badge variant="destructive">Red Flag</Badge>
                        ) : (
                          <Badge className="bg-yellow-500">Yellow Flag</Badge>
                        )}
                        <span className="text-sm text-slate-500">
                          {flag.days_overdue} day{flag.days_overdue !== 1 ? 's' : ''} overdue
                        </span>
                      </div>
                      
                      <p className="font-medium text-slate-800 mb-1">{flag.message}</p>
                      
                      <div className="text-sm text-slate-600 space-y-1">
                        <p className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Due: {new Date(flag.due_date).toLocaleDateString()}
                        </p>
                        {flag.responsible_staff_name && (
                          <p>Responsible: {flag.responsible_staff_name}</p>
                        )}
                        {flag.related_entity_name && (
                          <p>Related to: {flag.related_entity_name}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Navigate to the related entity
                          if (flag.related_entity_type === 'assessment') {
                            window.location.href = `/assessments?id=${flag.related_entity_id}`;
                          } else if (flag.related_entity_type === 'appointment') {
                            window.location.href = `/calendar?appointment=${flag.related_entity_id}`;
                          }
                        }}
                        data-testid={`flag-navigate-${flag.id}`}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Go to Item
                      </Button>
                      
                      {(user?.role === 'director' || user?.role === 'clinical_lead' || user?.role === 'admin') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResolve(flag.id)}
                          className="text-green-600 hover:text-green-700"
                          data-testid={`flag-resolve-${flag.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Dashboard flags summary widget
export function FlagsSummaryWidget() {
  const { api, user } = useAuth();
  const [summary, setSummary] = useState({ yellow_count: 0, red_count: 0, total: 0 });
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const [summaryRes, flagsRes] = await Promise.all([
        api.get('/clinical-flags/summary'),
        api.get('/clinical-flags/my-flags')
      ]);
      setSummary(summaryRes.data);
      setFlags(flagsRes.data);
    } catch (error) {
      console.error('Failed to fetch flags summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (user?.role === 'parent') return null;

  return (
    <>
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          summary.red_count > 0 
            ? 'border-red-300 hover:border-red-400' 
            : summary.yellow_count > 0 
              ? 'border-yellow-300 hover:border-yellow-400'
              : ''
        }`}
        onClick={() => summary.total > 0 && setDialogOpen(true)}
        data-testid="flags-summary-widget"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${summary.red_count > 0 ? 'text-red-500' : summary.yellow_count > 0 ? 'text-yellow-500' : 'text-slate-400'}`} />
            Documentation Flags
          </CardTitle>
        </CardHeader>
        <CardContent>
          {summary.total === 0 ? (
            <div className="text-center py-2">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-slate-500">All documentation up to date</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              {summary.red_count > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{summary.red_count}</div>
                  <div className="text-xs text-slate-500">Red Flags</div>
                </div>
              )}
              {summary.yellow_count > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{summary.yellow_count}</div>
                  <div className="text-xs text-slate-500">Yellow Flags</div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              My Clinical Flags
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {flags.map((flag) => (
              <Card 
                key={flag.id} 
                className={`${flag.severity === 'red' ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'}`}
              >
                <CardContent className="py-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {flag.severity === 'red' ? (
                          <Badge variant="destructive" className="text-xs">Red</Badge>
                        ) : (
                          <Badge className="bg-yellow-500 text-xs">Yellow</Badge>
                        )}
                        <span className="font-medium text-sm">{flag.patient_name}</span>
                      </div>
                      <p className="text-sm text-slate-600">{flag.message}</p>
                      <p className="text-xs text-slate-400 mt-1">{flag.days_overdue} day(s) overdue</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.location.href = `/patients/${flag.patient_id}`;
                      }}
                    >
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PatientFlagsBanner;

// Response Time Alerts Widget (12hr Yellow / 23hr Red - UK Healthcare Standards)
export function ResponseTimeAlertsWidget() {
  const { api, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/clinical-flags/response-time-alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch response time alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (user?.role === 'parent') return null;

  const redAlerts = alerts.filter(a => a.severity === 'red');
  const yellowAlerts = alerts.filter(a => a.severity === 'yellow');
  const hasAlerts = alerts.length > 0;

  return (
    <>
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          redAlerts.length > 0 
            ? 'border-red-300 bg-red-50 hover:border-red-400' 
            : yellowAlerts.length > 0 
              ? 'border-yellow-300 bg-yellow-50 hover:border-yellow-400'
              : 'border-green-200 bg-green-50'
        }`}
        onClick={() => hasAlerts && setDialogOpen(true)}
        data-testid="response-time-alerts-widget"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className={`w-4 h-4 ${redAlerts.length > 0 ? 'text-red-500 animate-pulse' : yellowAlerts.length > 0 ? 'text-yellow-500' : 'text-green-500'}`} />
            Patient Response Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasAlerts ? (
            <div className="text-center py-2">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-slate-500">All patients contacted</p>
            </div>
          ) : (
            <div className="space-y-2">
              {redAlerts.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
                  <span className="text-sm font-medium text-red-800">
                    Red Alert - 23h: {redAlerts.length} patient{redAlerts.length > 1 ? 's' : ''}
                  </span>
                  <AlertCircle className="w-4 h-4 text-red-600 animate-pulse" />
                </div>
              )}
              {yellowAlerts.length > 0 && (
                <div className="flex items-center justify-between p-2 bg-yellow-100 rounded-lg">
                  <span className="text-sm font-medium text-yellow-800">
                    Yellow Alert - 12h: {yellowAlerts.length} patient{yellowAlerts.length > 1 ? 's' : ''}
                  </span>
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Patient Response Time Alerts
              <span className="text-xs font-normal text-slate-500 ml-2">UK Healthcare Standard: 12hr/23hr</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No alerts at this time</p>
            ) : (
              alerts.map((alert) => (
                <Card 
                  key={alert.id} 
                  className={`${alert.severity === 'red' ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'}`}
                >
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {alert.severity === 'red' ? (
                            <Badge variant="destructive" className="text-xs">Red Alert - 23h</Badge>
                          ) : (
                            <Badge className="bg-yellow-500 text-xs">Yellow Alert - 12h</Badge>
                          )}
                          <span className="font-medium">{alert.patient_name}</span>
                          <span className="text-xs text-slate-500">({alert.reg_no})</span>
                        </div>
                        <p className="text-sm text-slate-600">{alert.message}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Registered: {new Date(alert.created_at).toLocaleString('en-GB')} 
                          ({alert.hours_since_registration}h ago)
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.location.href = `/patients/${alert.patient_id}`;
                        }}
                        data-testid={`response-alert-view-${alert.id}`}
                      >
                        <User className="w-4 h-4 mr-1" />
                        View Patient
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Missed Appointment Alerts Widget
export function MissedAppointmentsWidget() {
  const { api, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/clinical-flags/missed-appointments');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch missed appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await api.put(`/appointments/${appointmentId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Appointment marked as ${newStatus.replace('_', ' ')}`);
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update appointment status');
    }
  };

  if (loading) return null;
  if (user?.role === 'parent') return null;

  const hasAlerts = alerts.length > 0;

  return (
    <>
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          hasAlerts 
            ? 'border-red-300 bg-red-50 hover:border-red-400' 
            : 'border-green-200 bg-green-50'
        }`}
        onClick={() => hasAlerts && setDialogOpen(true)}
        data-testid="missed-appointments-widget"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className={`w-4 h-4 ${hasAlerts ? 'text-red-500' : 'text-green-500'}`} />
            Missed Appointments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasAlerts ? (
            <div className="text-center py-2">
              <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No missed appointments</p>
            </div>
          ) : (
            <div className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
              <span className="text-sm font-medium text-red-800">
                {alerts.length} missed appointment{alerts.length > 1 ? 's' : ''}
              </span>
              <Bell className="w-4 h-4 text-red-600 animate-pulse" />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-600" />
              Missed Appointments
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No missed appointments</p>
            ) : (
              alerts.map((alert) => (
                <Card key={alert.id} className="border-red-300 bg-red-50">
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="destructive" className="text-xs">DNA</Badge>
                          <span className="font-medium">{alert.patient_name}</span>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(alert.appointment_date).toLocaleDateString('en-GB')} at {alert.appointment_time}
                          </p>
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Therapist: {alert.therapist_name}
                          </p>
                          <p className="text-xs text-slate-400">
                            Type: {alert.appointment_type} | {alert.hours_since_missed}h since scheduled end
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus(alert.appointment_id, 'no_show')}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`mark-no-show-${alert.appointment_id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Mark DNA
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus(alert.appointment_id, 'completed')}
                          className="text-green-600 hover:text-green-700"
                          data-testid={`mark-attended-${alert.appointment_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Mark Attended
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Comprehensive Dashboard Alerts Banner
export function DashboardAlertsBanner() {
  const { api, user } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/clinical-flags/dashboard-alerts');
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !alerts || user?.role === 'parent') return null;
  
  const hasUrgent = alerts.total_urgent > 0;
  const hasResponseAlerts = alerts.response_time.red > 0 || alerts.response_time.yellow > 0;
  const hasMissedAppts = alerts.missed_appointments.count > 0;

  if (!hasUrgent && !hasResponseAlerts && !hasMissedAppts) return null;

  return (
    <Card className={`mb-6 border-2 ${alerts.total_urgent > 0 ? 'border-red-400 bg-gradient-to-r from-red-50 to-orange-50' : 'border-yellow-300 bg-yellow-50'}`} data-testid="dashboard-alerts-banner">
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          {alerts.total_urgent > 0 ? (
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600 animate-pulse" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          )}
          
          <div className="flex-1">
            <h3 className={`font-bold ${alerts.total_urgent > 0 ? 'text-red-800' : 'text-yellow-800'}`}>
              {alerts.total_urgent > 0 ? 'Urgent Alerts Requiring Immediate Attention' : 'Alerts Requiring Attention'}
            </h3>
            <div className="flex flex-wrap gap-4 mt-2">
              {alerts.response_time.red > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {alerts.response_time.red} Red Response Alerts (23h+)
                </Badge>
              )}
              {alerts.response_time.yellow > 0 && (
                <Badge className="bg-yellow-500 text-xs">
                  {alerts.response_time.yellow} Yellow Response Alerts (12h+)
                </Badge>
              )}
              {alerts.missed_appointments.count > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {alerts.missed_appointments.count} Missed Appointment{alerts.missed_appointments.count > 1 ? 's' : ''}
                </Badge>
              )}
              {alerts.documentation.red > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {alerts.documentation.red} Documentation Flags
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
